/**
 * Training reports — TRUS-486 §1.11.
 *
 * State reporting submits nothing anywhere; the obligation is that an administrator can
 * *search* training data with all the required fields present, and hand the result to
 * whoever asks. So this is a filter bar, a table, and a download.
 *
 * The Paycom export profile is deliberately absent from the format picker. The backend
 * returns 501 for it because the field format is still owed by the client, and offering a
 * button that always errors is worse than not offering it.
 */

import React, { useCallback, useEffect, useState } from 'react'
import { Download, FileSpreadsheet, Search } from 'lucide-react'
import { PageHeader, PageContainer, PageState, SectionHeader } from '@/components/weavein'
import { useOrgStaffOptions } from '@/components/forms/staffOptions'
import { Button, ErrorNote, FILTER_INPUT, INPUT, SortTh, TableShell, Td, Th, useSort } from './ui'
import {
  Competency,
  ReportRow,
  TRAINING_TYPES,
  TRAINING_TYPE_LABELS,
  competencyCoverage,
  downloadReport,
  formatDate,
  listCompetencies,
  listTrainings,
  mandatoryLabel,
  trainingReport,
} from './api'

interface Filters {
  // TRUS-562: filter the report by a staff member or a training. The API took both
  // (user_id, training_id); the page never offered them.
  user_id: string
  training_id: string
  training_type: string
  competency_id: string
  program_affiliation: string
  date_from: string
  date_to: string
  mandatory_only: boolean
}

const EMPTY_FILTERS: Filters = {
  user_id: '',
  training_id: '',
  training_type: '',
  competency_id: '',
  program_affiliation: '',
  date_from: '',
  date_to: '',
  mandatory_only: false,
}

export default function ReportsPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [rows, setRows] = useState<ReportRow[]>([])
  const [competencies, setCompetencies] = useState<Competency[]>([])
  const [coverage, setCoverage] = useState<{ mapped: number; unmapped: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState<string | null>(null)
  const staffOptions = useOrgStaffOptions({ includeOrgAdmins: true })
  const [trainingOptions, setTrainingOptions] = useState<{ id: number; title: string }[]>([])
  const [search, setSearch] = useState('')

  const params = useCallback(
    () => ({
      user_id: filters.user_id ? Number(filters.user_id) : undefined,
      training_id: filters.training_id ? Number(filters.training_id) : undefined,
      training_type: filters.training_type || undefined,
      competency_id: filters.competency_id ? Number(filters.competency_id) : undefined,
      program_affiliation: filters.program_affiliation || undefined,
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined,
      mandatory_only: filters.mandatory_only || undefined,
    }),
    [filters],
  )

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await trainingReport(params())
      setRows(data.rows)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not run the report.')
    } finally {
      setLoading(false)
    }
  }, [params])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    // Reference data, loaded once — the filter list and the coverage caption.
    void (async () => {
      try {
        const [c, cov, t] = await Promise.all([
          listCompetencies(),
          competencyCoverage(),
          // Retired trainings too: their completions are still in the report.
          listTrainings({ include_inactive: true }),
        ])
        setCompetencies(c.competencies)
        setTrainingOptions(
          t.trainings.map((x) => ({ id: x.id, title: x.title })).sort((a, b) => a.title.localeCompare(b.title)),
        )
        setCoverage({
          mapped: cov.competencies.filter((x) => x.trainings_mapped > 0).length,
          unmapped: cov.unmapped_trainings,
        })
      } catch {
        // Reference data is a nicety; the report still works without it.
      }
    })()
  }, [])

  const download = async (fmt: 'csv' | 'xlsx') => {
    setDownloading(fmt)
    setActionError(null)
    try {
      await downloadReport(fmt, params())
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not export the report.')
    } finally {
      setDownloading(null)
    }
  }


  // Client-side search and sort over the rows the filters returned.
  const searched = rows.filter((r) => {
    const needle = search.trim().toLowerCase()
    if (!needle) return true
    return [r.staff_name, r.employee_id, r.job_title, r.training_title]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(needle))
  })
  const { sorted: shown, sort, toggle } = useSort(
    searched,
    {
      employee_id: (r) => r.employee_id,
      name: (r) => r.staff_name,
      job_title: (r) => r.job_title,
      program: (r) => r.program_affiliation,
      training: (r) => r.training_title,
      date: (r) => r.training_date,
      hours: (r) => r.hours,
    },
    { key: 'date', dir: 'desc' },
  )
  return (
    <>
      <PageHeader
        eyebrow="Training · Reports"
        title="Training report"
        description="Search completions across staff, and export what you find."
        actions={
          <div className="flex gap-2">
            <Button onClick={() => void download('csv')} disabled={downloading !== null || rows.length === 0}>
              <Download className="h-4 w-4" />
              {downloading === 'csv' ? 'Preparing…' : 'CSV'}
            </Button>
            <Button onClick={() => void download('xlsx')} disabled={downloading !== null || rows.length === 0}>
              <FileSpreadsheet className="h-4 w-4" />
              {downloading === 'xlsx' ? 'Preparing…' : 'Excel'}
            </Button>
          </div>
        }
      />
      <PageContainer>
        <div className="space-y-4">
          {actionError && <ErrorNote message={actionError} onDismiss={() => setActionError(null)} />}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <select
              className={INPUT}
              value={filters.user_id}
              onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
              aria-label="Staff member"
            >
              <option value="">All staff</option>
              {staffOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>

            <select
              className={INPUT}
              value={filters.training_id}
              onChange={(e) => setFilters({ ...filters, training_id: e.target.value })}
              aria-label="Training"
            >
              <option value="">All trainings</option>
              {trainingOptions.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.title}
                </option>
              ))}
            </select>

            <select
              className={INPUT}
              value={filters.training_type}
              onChange={(e) => setFilters({ ...filters, training_type: e.target.value })}
              aria-label="Training type"
            >
              <option value="">All training types</option>
              {TRAINING_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TRAINING_TYPE_LABELS[t]}
                </option>
              ))}
            </select>

            <select
              className={INPUT}
              value={filters.competency_id}
              onChange={(e) => setFilters({ ...filters, competency_id: e.target.value })}
              aria-label="Core competency"
            >
              <option value="">All competencies</option>
              {competencies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <input
              className={INPUT}
              placeholder="Program affiliation"
              value={filters.program_affiliation}
              onChange={(e) => setFilters({ ...filters, program_affiliation: e.target.value })}
              aria-label="Program affiliation"
            />

            <label className="flex flex-col text-[12px] text-ink-500">
              From
              <input
                className={INPUT}
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              />
            </label>

            <label className="flex flex-col text-[12px] text-ink-500">
              To
              <input
                className={INPUT}
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              />
            </label>

            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 text-[13px] text-ink">
                <input
                  type="checkbox"
                  checked={filters.mandatory_only}
                  onChange={(e) => setFilters({ ...filters, mandatory_only: e.target.checked })}
                />
                Mandatory only
              </label>
              <Button onClick={() => setFilters(EMPTY_FILTERS)} variant="ghost">
                Clear
              </Button>
            </div>
          </div>

          {coverage && coverage.unmapped > 0 && (
            <div className="rounded-[10px] border border-v2border bg-paper-2 px-4 py-3 text-[13px] text-ink-500">
              Filtering by competency only finds trainings that have been mapped to one.{' '}
              <strong className="text-ink">{coverage.unmapped}</strong> training
              {coverage.unmapped === 1 ? ' is' : 's are'} not mapped yet, so their completions will not appear under
              any competency filter.
            </div>
          )}

          {loading ? (
            <PageState variant="loading" title="Running the report" />
          ) : error ? (
            <PageState
              variant="error"
              title="Could not run the report"
              message={error}
              action={<Button onClick={() => void load()}>Retry</Button>}
            />
          ) : rows.length === 0 ? (
            <PageState
              variant="empty"
              icon={<Search className="h-5 w-5 text-ink-400" />}
              title="No completions match"
              message="Widen the filters, or record a completion first — the report reads from the completion ledger."
            />
          ) : (
            <>
              <input
                type="search"
                className={`${FILTER_INPUT} w-full sm:max-w-sm`}
                placeholder="Search name, Employee ID, job title or training"
                aria-label="Search the report"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <SectionHeader
                title={
                  shown.length === rows.length
                    ? `${rows.length} completion${rows.length === 1 ? '' : 's'}`
                    : `${shown.length} of ${rows.length} completions`
                }
              />
              <TableShell>
                <thead>
                  <tr>
                    <SortTh label="Employee ID" sortKey="employee_id" sort={sort} onSort={toggle} />
                    <SortTh label="Name" sortKey="name" sort={sort} onSort={toggle} />
                    <SortTh label="Job title" sortKey="job_title" sort={sort} onSort={toggle} />
                    <SortTh label="Program" sortKey="program" sort={sort} onSort={toggle} />
                    <SortTh label="Training" sortKey="training" sort={sort} onSort={toggle} />
                    <Th>Required</Th>
                    <SortTh label="Completed" sortKey="date" sort={sort} onSort={toggle} />
                    <SortTh label="Hours" sortKey="hours" sort={sort} onSort={toggle} />
                    <Th>Reported by</Th>
                    <Th>Certificate</Th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 && (
                    <tr>
                      <Td colSpan={10} className="text-ink-500">
                        No completions match that search.
                      </Td>
                    </tr>
                  )}
                  {shown.map((r) => (
                    <tr key={r.record_id} className="hover:bg-paper-2">
                      <Td className="whitespace-nowrap font-mono text-[12px]">{r.employee_id ?? '—'}</Td>
                      <Td className="whitespace-nowrap font-medium">{r.staff_name ?? `Staff #${r.user_id}`}</Td>
                      <Td>{r.job_title ?? '—'}</Td>
                      <Td>{r.program_affiliation ?? '—'}</Td>
                      <Td>{r.training_title}</Td>
                      <Td>{mandatoryLabel(r.is_mandatory)}</Td>
                      <Td className="whitespace-nowrap">{formatDate(r.training_date)}</Td>
                      <Td className="tabular-nums">{r.hours === null ? '—' : r.hours}</Td>
                      <Td>{r.reported_by === 'self' ? 'Self' : 'Admin'}</Td>
                      <Td className="text-ink-500">{r.certificate_filename ?? '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </TableShell>
            </>
          )}
        </div>
      </PageContainer>
    </>
  )
}
